// El parte del día, dibujado como imagen.
//
// ── Por qué ──
//
// «El cliente quiere que le llegue la imagen al WhatsApp de Gabriel en vez de
// texto.»
//
// Un enlace de WhatsApp SOLO puede llevar texto: no hay forma de que un botón
// abra el chat con una foto ya puesta. Así que la imagen se dibuja aquí, se
// copia al portapapeles, y el muchacho la pega en el chat con Ctrl+V. Un toque
// más, y a Gabriel le llega como foto.
//
// ── Por qué dibujada y no una foto de la pantalla ──
//
// Una captura arrastra el menú, los filtros y el ancho del monitor; en el
// teléfono de Gabriel eso se ve como una miniatura ilegible. Dibujarla deja
// decidir el tamaño de la letra, y el número que él pide —los kilos por tina—
// sale grande de verdad.
//
// ── De dónde salen los números ──
//
// De `totalesDelDia` y `compararRutas`, las MISMAS de las que sale el mensaje
// de texto. Dos cuentas del mismo día terminan diciendo cifras distintas, y
// entonces no se le puede creer a ninguna.

import { totalesDelDia, diaLargo } from './reporteRutas';
import { compararRutas, calificar } from './ruta';
import { promedio, conComa, peso } from './formato';

// La paleta del programa. Va con los números puestos porque el canvas no lee
// las variables del CSS, y el parte tiene que verse igual desde cualquier
// máquina —y en modo oscuro también—.
const C = {
  papel: '#FFFFFF',
  hondo: '#F2F5F5',
  tinta: '#14201E',
  media: '#48605B',
  tenue: '#7A8D89',
  raya: '#DDE4E3',
  acento: '#A9661C',
  bueno: '#2F7A52',
  ojo: '#9A6A11',
  malo: '#A8382A',
  oro: '#C9971F',
  plata: '#9BA5A8',
  bronce: '#B0743D',
};

const FUENTE = '"Segoe UI", system-ui, -apple-system, Roboto, Helvetica, Arial, sans-serif';
const f = (tam, grueso = 400) => `${grueso} ${tam}px ${FUENTE}`;

const ANCHO = 1000;
const MARGEN = 56;
const ESCALA = 2;          // para que no se vea pixelada al ampliarla

// El color de una nota, igual que en la pantalla.
const colorDe = (cumple) => {
  if (cumple === null) return C.tenue;
  const p = calificar(cumple).peso;
  return p <= 2 ? C.malo : (p === 3 ? C.ojo : C.bueno);
};

// Un rectángulo con las esquinas redondas. `roundRect` no está en todos los
// navegadores viejos y esta es la pieza de la que cuelga todo el dibujo.
function caja(g, x, y, an, al, r) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + an, y, x + an, y + al, r);
  g.arcTo(x + an, y + al, x, y + al, r);
  g.arcTo(x, y + al, x, y, r);
  g.arcTo(x, y, x + an, y, r);
  g.closePath();
}

// El puesto, en un círculo. Los tres primeros con su color.
//
// Va dibujado y no con los emojis 🥇🥈🥉 a propósito: el parte se arma en el
// PC del muchacho, y un emoji que esa máquina no tenga sale como un cuadro
// vacío. Un círculo con su número se ve igual en todas partes.
function puesto(g, x, y, n, flojo) {
  const col = flojo ? C.malo : [C.oro, C.plata, C.bronce][n - 1] || C.tenue;
  g.fillStyle = col;
  g.beginPath();
  g.arc(x + 17, y + 17, 17, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = '#FFFFFF';
  g.font = f(18, 700);
  g.textAlign = 'center';
  g.fillText(flojo ? '!' : String(n), x + 17, y + 24);
  g.textAlign = 'left';
}

// Lo que va a salir dibujado. Se calcula aparte del dibujo para poder
// medir el alto antes de pintar: un canvas se estira, no crece.
export function armarParte(filas, opciones = {}) {
  const lista = Array.isArray(filas) ? filas : [];
  const t = totalesDelDia(lista);

  // Sin producción no se dibuja un parte que diga «0 kilos»: eso se lee como
  // que el programa falló, no como que ese día no se cuajó.
  if (t.cuajadas === 0 || t.kilos <= 0) return null;

  const enOrden = compararRutas(lista).filter(x => x.lectura);
  const flojas = enOrden.filter(r => r.lectura.cumple !== null &&
                                     calificar(r.lectura.cumple).peso <= 2);
  const quietas = lista.filter(r => !(Number(r.cuajadas) > 0)).map(r => r.nombre);

  return {
    negocio: (opciones.negocio || '').trim(),
    dia: opciones.dia,
    total: t,
    rutas: enOrden,
    flojas,
    quietas,
  };
}

// Dibuja el parte y devuelve el canvas.
export function dibujarParte(datos) {
  if (!datos) return null;
  const { negocio, dia, total, rutas, flojas, quietas } = datos;

  // El alto, contado antes de pintar.
  const ALTO_RUTA = 62;
  let alto = MARGEN + (negocio ? 42 : 0) + 34 + 30;   // cabecera
  alto += 128;                                        // el número grande
  alto += 54;                                         // el renglón de abajo
  if (rutas.length > 1) alto += 46 + rutas.length * ALTO_RUTA;
  if (flojas.length > 0) alto += 20 + flojas.length * 44;
  if (quietas.length > 0) alto += 46;
  alto += MARGEN;

  const canvas = document.createElement('canvas');
  canvas.width = ANCHO * ESCALA;
  canvas.height = Math.round(alto) * ESCALA;
  const g = canvas.getContext('2d');
  g.scale(ESCALA, ESCALA);
  g.textBaseline = 'alphabetic';

  g.fillStyle = C.papel;
  g.fillRect(0, 0, ANCHO, alto);

  // La franja de arriba, que es lo único de adorno que lleva.
  g.fillStyle = C.acento;
  g.fillRect(0, 0, ANCHO, 8);

  let y = MARGEN + 12;

  if (negocio) {
    g.fillStyle = C.acento;
    g.font = f(30, 700);
    g.fillText(negocio, MARGEN, y);
    y += 42;
  }
  g.fillStyle = C.media;
  g.font = f(22, 400);
  g.fillText(`Producción del ${diaLargo(dia)}`, MARGEN, y);
  y += 46;

  // ── EL NÚMERO. Es el que Gabriel pide y por eso ocupa lo que ocupa ────────
  const L = total.lectura;
  g.fillStyle = C.tinta;
  g.font = f(96, 700);
  const num = promedio(L.porTina);
  g.fillText(num, MARGEN, y + 74);
  const anchoNum = g.measureText(num).width;
  g.fillStyle = C.media;
  g.font = f(26, 400);
  g.fillText('kg por tina', MARGEN + anchoNum + 16, y + 74);

  // La nota, a la derecha y con su color.
  if (L.cumple !== null) {
    const col = colorDe(L.cumple);
    const txt = `${conComa(L.cumple)}%`;
    const nombre = calificar(L.cumple).nombre;
    g.font = f(52, 700);
    const an = Math.max(g.measureText(txt).width, 0) + 44;
    g.fillStyle = col + '18';
    caja(g, ANCHO - MARGEN - an, y + 8, an, 82, 14);
    g.fill();
    g.fillStyle = col;
    g.textAlign = 'center';
    g.fillText(txt, ANCHO - MARGEN - an / 2, y + 58);
    g.font = f(16, 600);
    g.fillText(nombre.toUpperCase(), ANCHO - MARGEN - an / 2, y + 80);
    g.textAlign = 'left';
  }
  y += 128;

  g.fillStyle = C.tenue;
  g.font = f(21, 400);
  g.fillText(`${peso(total.litros, 'L')} · ${conComa(L.tinas)} tinas · ` +
             `${peso(total.kilos, 'kg')} de queso`, MARGEN, y + 16);
  y += 54;

  // ── Cada ruta ────────────────────────────────────────────────────────────
  if (rutas.length > 1) {
    g.fillStyle = C.tinta;
    g.font = f(20, 700);
    g.fillText('POR RUTA', MARGEN, y + 14);
    y += 46;

    rutas.forEach((r, i) => {
      const RL = r.lectura;
      const flojo = RL.cumple !== null && calificar(RL.cumple).peso <= 2;
      const col = colorDe(RL.cumple);

      g.fillStyle = i % 2 === 0 ? C.hondo : C.papel;
      caja(g, MARGEN - 14, y - 8, ANCHO - (MARGEN - 14) * 2, ALTO_RUTA - 6, 10);
      g.fill();

      puesto(g, MARGEN, y + 2, i + 1, flojo);

      g.fillStyle = C.tinta;
      g.font = f(24, 600);
      g.fillText(r.nombre, MARGEN + 52, y + 27);

      // Tres columnas con su sitio fijo, de derecha a izquierda: la nota, el
      // rótulo y el número. Antes el rótulo se colocaba a partir del número, y
      // con una nota de cuatro cifras se le montaba encima.
      const COL_NOTA = ANCHO - MARGEN;              // 105,6%
      const COL_ROTULO = COL_NOTA - 120;            // kg/tina
      const COL_KILOS = COL_ROTULO - 58;            // 6,60

      g.textAlign = 'right';
      g.fillStyle = C.tenue;
      g.font = f(16, 400);
      g.fillText('kg/tina', COL_ROTULO, y + 27);

      g.fillStyle = C.tinta;
      g.font = f(26, 700);
      g.fillText(promedio(RL.porTina), COL_KILOS, y + 27);

      if (RL.cumple !== null) {
        g.fillStyle = col;
        g.font = f(24, 700);
        g.fillText(`${conComa(RL.cumple)}%`, COL_NOTA, y + 27);
      }
      g.textAlign = 'left';
      y += ALTO_RUTA;
    });
  }

  // ── La que se quedó corta, con nombre y kilos ─────────────────────────────
  //
  // Una lista de números no mueve a nadie. Nombrar la ruta y decir cuántos
  // kilos faltaron es lo que hace que Gabriel llame HOY, y no el mes que
  // viene cuando ya no se puede revisar esa leche.
  if (flojas.length > 0) {
    y += 20;
    flojas.forEach((r) => {
      const faltaron = Math.round((Number(r.kilos_esperados) || 0) -
                                  (Number(r.kilos_medibles) || 0));
      g.fillStyle = C.malo + '14';
      caja(g, MARGEN - 14, y - 12, ANCHO - (MARGEN - 14) * 2, 40, 10);
      g.fill();
      g.fillStyle = C.malo;
      g.fillRect(MARGEN - 14, y - 12, 5, 40);
      g.font = f(21, 600);
      g.fillText(`${r.nombre} quedó en ${conComa(r.lectura.cumple)}%` +
                 (faltaron > 0 ? `: faltaron ${peso(faltaron, 'kg')}` : ''),
                 MARGEN + 4, y + 14);
      y += 44;
    });
  }

  // Las que no cuajaron. Su ausencia es información: si LA FE lleva tres días
  // sin aparecer, alguien tiene que preguntarse por qué.
  if (quietas.length > 0) {
    y += 12;
    g.fillStyle = C.tenue;
    g.font = f(19, 400);
    g.fillText(`Sin cuajar: ${quietas.join(', ')}`, MARGEN, y + 14);
  }

  return canvas;
}

// La imagen, lista para pegar o para guardar.
export function imagenDelParte(filas, opciones = {}) {
  const canvas = dibujarParte(armarParte(filas, opciones));
  if (!canvas) return Promise.resolve(null);
  return new Promise((listo) => canvas.toBlob(listo, 'image/png'));
}

// Copiarla al portapapeles, que es lo que de verdad se usa: se copia aquí y
// se pega en el chat de WhatsApp con Ctrl+V.
//
// Devuelve `false` en vez de reventar cuando el navegador no deja —Firefox no
// tiene ClipboardItem, y sin HTTPS tampoco funciona—. Quien llama enseña
// entonces el botón de descargar, que sirve en todas partes.
export async function copiarImagen(blob) {
  try {
    if (!blob || !navigator.clipboard || typeof window.ClipboardItem !== 'function') {
      return false;
    }
    await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
    return true;
  } catch {
    return false;
  }
}

// Cómo se va a llamar el archivo. Con la fecha adentro, porque el que baja el
// parte de tres días seguidos termina con «parte(1).png» y no sabe cuál es
// cuál.
export function nombreDelArchivo(dia) {
  const d = String(dia || '').slice(0, 10) || 'hoy';
  return `parte-${d}.png`;
}
